import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { FiImage, FiCheckCircle, FiAlertTriangle, FiInfo, FiLock, FiTrash2 } from 'react-icons/fi';
import { mediaAIService } from '../../services/apiService';
import { useAuthStore } from '../../utils/store';
import { useT } from '../../utils/i18n';

const StatusBadge = ({ configured }) => {
  const t = useT();
  return configured ? (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
      <FiCheckCircle size={13} />
      {t('mediaProviders.connected')}
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
      <FiAlertTriangle size={13} />
      {t('mediaProviders.notConnected')}
    </span>
  );
};

// What each video provider's key is called on its site, and where to get it.
const VIDEO_PROVIDER_INFO = {
  google:  { name: 'Google (Veo, Gemini Omni Flash)', keyName: 'Gemini API key', where: 'aistudio.google.com → Get API key' },
  kling:   { name: 'Kling', keyName: 'API Key', where: 'Kling AI developer console → API Keys' },
  minimax: { name: 'MiniMax', keyName: 'API Key (pay-as-you-go)', where: 'platform.minimax.io → API Keys' },
  alibaba: { name: 'Alibaba Wan', keyName: 'Model Studio API key', where: 'Alibaba Cloud Model Studio (Singapore) → API Keys' },
};

/** One video provider: its models and prices, and its key. */
const VideoProviderCard = ({ id, status, models, onSettings }) => {
  const t = useT();
  const info = VIDEO_PROVIDER_INFO[id];
  const [apiKey, setApiKey] = useState('');
  const [baseUrl, setBaseUrl] = useState(status?.baseUrl || '');
  const [saving, setSaving] = useState(false);
  const configured = status?.configured;

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      onSettings(await mediaAIService.saveVideoProviderKey(id, { apiKey, baseUrl }));
      setApiKey('');
      toast.success(t('mediaProviders.keySaved'));
    } catch (err) {
      toast.error(err.response?.data?.message || t('mediaProviders.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(t('mediaProviders.removeVideoConfirm', { provider: info.name }))) return;
    try {
      onSettings(await mediaAIService.removeVideoProviderKey(id));
      toast.success(t('mediaProviders.removed'));
    } catch (err) {
      toast.error(err.response?.data?.message || t('mediaProviders.saveFailed'));
    }
  };

  return (
    <div className="card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <h3 className="text-base font-semibold text-gray-900">{info.name}</h3>
        <StatusBadge configured={configured} />
      </div>

      <div className="mt-3 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-gray-400">
              <th className="font-medium py-1 pr-3">{t('mediaProviders.model')}</th>
              <th className="font-medium py-1 pr-3 text-right">720p</th>
              <th className="font-medium py-1 text-right">1080p</th>
            </tr>
          </thead>
          <tbody>
            {models.map((m) => (
              <tr key={m.id} className="border-t border-gray-100">
                <td className="py-1.5 pr-3 text-gray-700">{m.name}</td>
                <td className="py-1.5 pr-3 text-right tabular-nums text-gray-600">${m.price['720p'].toFixed(3)}/s</td>
                <td className="py-1.5 text-right tabular-nums text-gray-600">${m.price['1080p'].toFixed(3)}/s</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <form onSubmit={save} className="mt-4 space-y-3">
        {configured && (
          <p className="text-sm text-gray-600">
            {status.source === 'server'
              ? t('mediaProviders.usingServerKey')
              : <>{t('mediaProviders.storedKey')} <span className="font-mono text-gray-900">{status.keyHint}</span></>}
            {status.updatedBy && <span className="text-gray-400"> · {t('mediaProviders.updatedBy', { name: status.updatedBy })}</span>}
          </p>
        )}
        <div>
          <label htmlFor={`video-key-${id}`} className="block text-sm font-medium text-gray-700 mb-1">{info.keyName}</label>
          <input
            id={`video-key-${id}`}
            type="password"
            autoComplete="off"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            className="input w-full font-mono"
            placeholder={configured ? t('mediaProviders.replaceKey') : t('mediaProviders.apiKeyPlaceholder')}
          />
          <p className="text-xs text-gray-400 mt-1">{t('mediaProviders.whereToGet', { where: info.where })}</p>
        </div>
        {id === 'alibaba' && (
          <div>
            <label htmlFor="video-base-alibaba" className="block text-sm font-medium text-gray-700 mb-1">
              {t('mediaProviders.alibabaHost')}
              <span className="text-gray-400 font-normal ml-1">{t('mediaProviders.optional')}</span>
            </label>
            <input
              id="video-base-alibaba"
              type="url"
              value={baseUrl}
              onChange={(e) => setBaseUrl(e.target.value)}
              className="input w-full font-mono"
              placeholder="https://dashscope-intl.aliyuncs.com"
            />
            <p className="text-xs text-gray-400 mt-1">{t('mediaProviders.alibabaHostHint')}</p>
          </div>
        )}
        <div className="flex items-center justify-between gap-3 pt-2 border-t border-gray-100 flex-wrap">
          {configured && status.source === 'settings' ? (
            <button type="button" onClick={remove} className="btn btn-ghost text-danger-600 hover:bg-danger-50 flex items-center gap-2">
              <FiTrash2 size={14} />
              {t('mediaProviders.remove')}
            </button>
          ) : <span />}
          <button type="submit" disabled={saving || !apiKey.trim()} className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed">
            {saving ? t('mediaProviders.saving') : t('mediaProviders.save')}
          </button>
        </div>
      </form>
    </div>
  );
};

/** The team's daily video budget, and how much of today's has been spent. */
const VideoBudgetCard = ({ budget, onSettings }) => {
  const t = useT();
  const [value, setValue] = useState(String(budget?.dailyUSD ?? ''));
  const [saving, setSaving] = useState(false);
  const spent = budget?.spentTodayUSD || 0;
  const daily = budget?.dailyUSD || 0;
  const share = daily > 0 ? Math.min(100, (spent / daily) * 100) : 100;

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      onSettings(await mediaAIService.saveVideoBudget(Number(value)));
      toast.success(t('mediaProviders.budgetSaved'));
    } catch (err) {
      toast.error(err.response?.data?.message || t('mediaProviders.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card">
      <h3 className="text-base font-semibold text-gray-900">{t('mediaProviders.videoBudget')}</h3>
      <p className="text-sm text-gray-500 mt-0.5">{t('mediaProviders.videoBudgetDesc')}</p>
      <div className="mt-3">
        <div className="flex justify-between text-sm text-gray-600 tabular-nums">
          <span>{t('mediaProviders.spentToday', { spent: spent.toFixed(2) })}</span>
          <span>${daily.toFixed(2)}</span>
        </div>
        <div className="mt-1 h-2 rounded-full bg-gray-100 overflow-hidden">
          <div className={`h-full ${share >= 90 ? 'bg-danger-500' : 'bg-primary-500'}`} style={{ width: `${share}%` }} />
        </div>
      </div>
      <form onSubmit={save} className="mt-4 flex items-end gap-3 flex-wrap">
        <div>
          <label htmlFor="video-budget" className="block text-sm font-medium text-gray-700 mb-1">{t('mediaProviders.dailyLimit')}</label>
          <div className="flex items-center gap-1.5">
            <span className="text-gray-500">$</span>
            <input
              id="video-budget"
              type="number"
              min="0"
              step="0.5"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              className="input w-32 tabular-nums"
            />
          </div>
        </div>
        <button type="submit" disabled={saving || value === '' || Number(value) === daily} className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed">
          {saving ? t('mediaProviders.saving') : t('mediaProviders.save')}
        </button>
      </form>
    </div>
  );
};

// Admin-only: credentials for the providers behind Media AI. Secrets are
// write-only — the page shows whether one is stored and its last four
// characters, never the value.
export const MediaAIProviders = () => {
  const t = useT();
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';

  const [freepik, setFreepik] = useState(null);
  const [videoSettings, setVideoSettings] = useState(null); // { video, videoModels, videoBudget }
  const [apiKey, setApiKey] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const applySettings = (settings) => {
    setFreepik(settings.freepik);
    setVideoSettings({ video: settings.video, videoModels: settings.videoModels || [], videoBudget: settings.videoBudget });
  };

  useEffect(() => {
    if (!isAdmin) { setLoading(false); return; }
    mediaAIService.getSettings()
      .then(applySettings)
      .catch((e) => toast.error(e.response?.data?.message || e.message))
      .finally(() => setLoading(false));
  }, [isAdmin]);

  if (!isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center text-center h-64 max-w-md mx-auto gap-3">
        <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
          <FiLock size={20} className="text-gray-400" />
        </div>
        <h1 className="text-lg font-semibold text-gray-900">{t('mediaProviders.restrictedTitle')}</h1>
        <p className="text-sm text-gray-500">{t('mediaProviders.restrictedDesc')}</p>
      </div>
    );
  }

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const settings = await mediaAIService.saveFreepikCredentials({ apiKey, webhookSecret });
      applySettings(settings);
      setApiKey('');
      setWebhookSecret('');
      toast.success(t('mediaProviders.saved'));
    } catch (err) {
      toast.error(err.response?.data?.message || t('mediaProviders.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    if (!window.confirm(t('mediaProviders.removeConfirm'))) return;
    try {
      const settings = await mediaAIService.removeFreepikCredentials();
      applySettings(settings);
      toast.success(t('mediaProviders.removed'));
    } catch (err) {
      toast.error(err.response?.data?.message || t('mediaProviders.saveFailed'));
    }
  };

  const configured = freepik?.configured;
  const canSave = !saving && (apiKey.trim() || (configured && webhookSecret.trim()));

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-center gap-3">
        <FiImage size={22} className="text-primary-600" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('mediaProviders.title')}</h1>
          <p className="text-sm text-gray-500 mt-0.5">{t('mediaProviders.subtitle')}</p>
        </div>
      </div>

      {/* OpenAI — configured elsewhere; listed so the picture is complete */}
      <div className="card">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">OpenAI</h2>
            <p className="text-sm text-gray-500 mt-0.5">{t('mediaProviders.openaiUse')}</p>
          </div>
        </div>
        <p className="mt-3 flex items-start gap-2 text-sm text-gray-600">
          <FiInfo size={15} className="mt-0.5 flex-shrink-0 text-gray-400" />
          <span>{t('mediaProviders.openaiNote')}</span>
        </p>
      </div>

      {/* Freepik */}
      <div className="card">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Freepik</h2>
            <p className="text-sm text-gray-500 mt-0.5">{t('mediaProviders.freepikUse')}</p>
          </div>
          {!loading && (
            configured ? (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                <FiCheckCircle size={13} />
                {t('mediaProviders.connected')}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
                <FiAlertTriangle size={13} />
                {t('mediaProviders.notConnected')}
              </span>
            )
          )}
        </div>

        {loading ? (
          <p className="mt-4 text-sm text-gray-500">{t('mediaProviders.loading')}</p>
        ) : (
          <form onSubmit={handleSave} className="mt-4 space-y-4">
            {configured && (
              <p className="text-sm text-gray-600">
                {t('mediaProviders.storedKey')} <span className="font-mono text-gray-900">{freepik.keyHint}</span>
                {freepik.updatedBy && (
                  <span className="text-gray-400">
                    {' '}· {t('mediaProviders.updatedBy', { name: freepik.updatedBy })}
                    {freepik.updatedAt ? `, ${new Date(freepik.updatedAt).toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' })}` : ''}
                  </span>
                )}
              </p>
            )}

            <div>
              <label htmlFor="freepik-api-key" className="block text-sm font-medium text-gray-700 mb-1">
                {t('mediaProviders.apiKey')}{!configured && ' *'}
              </label>
              <input
                id="freepik-api-key"
                type="password"
                autoComplete="off"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                className="input w-full font-mono"
                placeholder={configured ? t('mediaProviders.keepBlank') : t('mediaProviders.apiKeyPlaceholder')}
              />
            </div>

            <div>
              <label htmlFor="freepik-webhook-secret" className="block text-sm font-medium text-gray-700 mb-1">
                {t('mediaProviders.webhookSecret')}
                <span className="text-gray-400 font-normal ml-1">{t('mediaProviders.optional')}</span>
              </label>
              <input
                id="freepik-webhook-secret"
                type="password"
                autoComplete="off"
                value={webhookSecret}
                onChange={(e) => setWebhookSecret(e.target.value)}
                className="input w-full font-mono"
                placeholder={freepik?.hasWebhookSecret ? t('mediaProviders.keepBlank') : ''}
              />
              <p className="text-xs text-gray-400 mt-1">{t('mediaProviders.webhookHint')}</p>
            </div>

            <div className="flex items-center justify-between gap-3 pt-2 border-t border-gray-100 flex-wrap">
              {configured ? (
                <button type="button" onClick={handleRemove} className="btn btn-ghost text-danger-600 hover:bg-danger-50 flex items-center gap-2">
                  <FiTrash2 size={14} />
                  {t('mediaProviders.remove')}
                </button>
              ) : <span />}
              <button type="submit" disabled={!canSave} className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed">
                {saving ? t('mediaProviders.checking') : t('mediaProviders.save')}
              </button>
            </div>
          </form>
        )}
      </div>

      {/* Video: daily budget, then one card per provider */}
      {!loading && videoSettings && (
        <section className="space-y-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{t('mediaProviders.videoTitle')}</h2>
            <p className="text-sm text-gray-500 mt-0.5">{t('mediaProviders.videoDesc')}</p>
          </div>
          <VideoBudgetCard budget={videoSettings.videoBudget} onSettings={applySettings} />
          {Object.keys(VIDEO_PROVIDER_INFO).map((id) => (
            <VideoProviderCard
              key={id}
              id={id}
              status={videoSettings.video?.[id]}
              models={videoSettings.videoModels.filter((m) => m.provider === id)}
              onSettings={applySettings}
            />
          ))}
        </section>
      )}
    </div>
  );
};

export default MediaAIProviders;
